import { useContext, useEffect, useState } from 'react';
import LegoAdminPageContext from '../../legoAdminPageContext';
import _ from 'lodash';
import SpecSourceBadge from '../components/SpecSourceBadge';
import LegoContextSummary from '../../../components/lego/LegoContextSummary';
import { IconButton } from '../../../components/common/IconButton';


function shortenCommonWords(text) {
  return text
    .replace('4-Wheel Drive', '4x4')
    .replace('Rear-Wheel Drive', 'RWD')
    .replace('All-Wheel Drive', 'AWD')
    .replace('Front-Wheel Drive', 'FWD')
    .replace('Double Cab', 'D.Cab')
    .replace('4-Cyl. Turbo Engine', '')
    .replace('Hybrid Powertrain', '⚡')
    .replace(/(\d)-ft. bed/, '$1\'bed')
    .replace('4-Cyl. Engine', '')
}

export default function SpecsGroupSelector({ context, initialSelection, multi = false, forceRefresh = null, filter = null, onGroupSelected, highlightedIds = null, vertical = false }) {
  const { page } = useContext(LegoAdminPageContext);

  const [objects, setObjects] = useState(null);
  const [textFilter, setTextFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(_.compact(multi ? initialSelection : [initialSelection]));

  const selectGroup = (group, ctrlKey) => {
    if (selected.includes(group._id)) {
      const newIds = _.without(selected, group._id);
      setSelected(newIds);
      onGroupSelected(multi ? newIds : undefined);
    } else {
      if (multi) {
        const newIds = ctrlKey ? [...selected, group._id] : [group._id];
        setSelected(newIds);
        onGroupSelected(newIds);
      } else {
        onGroupSelected(group);
        setSelected([group._id]);
      }
    }
  }

  const toggleGroup = (ids) => {
    let newSelection;
    if(_.difference(ids, selected).length === 0) {
      newSelection = _.difference(selected, ids);
    } else {
      newSelection = _.union(selected, ids);
    }
    setSelected(newSelection);
    onGroupSelected(newSelection);
  }

  // When initialSelection changes, reset the selected groups
  useEffect(() => {
    setSelected(_.compact(multi ? initialSelection : [initialSelection]));
  }, [JSON.stringify(initialSelection)]);

  useEffect(() => {
    setLoading(true);

    if (context?.modelId) {
      filter = { ...filter, 'context.modelId': context.modelId };
    }
    if(context?.make) {
      filter = { ...filter, 'context.modelId': { $regex: `^${context.make}-` } };
    }

    if(!_.isEmpty(filter)) {
      page.service('services/cerokm/specsgroup').find({ query: { ...filter } }).then(setObjects).finally(() => setLoading(false));
    }
  }, [forceRefresh, context, filter]);


  if (objects) {
    let groupedByBasedOn = _.groupBy(objects, 'basedOn');

    let noGroup = _.sortBy(objects.filter(o => !o.basedOn), 'name');

    let modelIdYear = _.groupBy(noGroup, o => JSON.stringify(_.omit(o.context, 'trim')));

    return <div className={`mt-1 overflow-auto ${vertical ? 'd-flex flex-column' : 'no-wrap'}`}>
      {_.map(modelIdYear, (topTrims, modelContext) => {
        return <div key={modelContext} className={`rounded bg-light border p-1 overflow-auto inline-block align-top ${vertical ? 'w-100' : 'mr-1'}`}>
          <div className={'p-1 font-weight-bold'}>
            <LegoContextSummary inline context={_.omit(topTrims[0].context, 'trim')} />

            { multi ? <IconButton icon={selected.length === objects.length ? 'check_box' : (selected.length > 0 ? 'indeterminate_check_box' : 'check_box_outline_blank')} onClick={() => toggleGroup(_.flatMap(topTrims, o => groupedByBasedOn[o._id].map(o => o._id)))}>Select All</IconButton> : null}
          </div>

          <div className={`d-flex align-items-start rounded ${vertical ? 'flex-column' : ''}`}>
            {
              topTrims.map(o => {
                const allIds = _.map(groupedByBasedOn[o._id], o => o._id);
                const covered = _.difference(allIds, selected).length;
                const selectionIcon = covered ? (covered === allIds.length ? 'check_box_outline_blank' : 'indeterminate_check_box') :'check_box';

                return <div key={o._id} className={`p-05 m-05 bg-dark text-white border rounded px-1 ${vertical ? 'w-100' : ''}`}>
                  <div className={'d-flex align-items-center justify-content-between mb-1'}>
                    {multi ? <IconButton icon={selectionIcon} onClick={() => toggleGroup(allIds)}>
                      <span className='text-white'>{o.name}</span>
                      </IconButton> : <span className='text-white'>{o.name}</span>}

                    <SpecSourceBadge small sourceId={o.sourceId} showContext={false} className='ml-1' />
                  </div>

                  {_.map(groupedByBasedOn[o._id], (subtrim) => {
                    const isSelected = selected.includes(subtrim._id);
                    const isDisabled = subtrim.disabled;

                    return <div key={subtrim._id}>
                      <span className={`monospace btn btn-xs btn-${isSelected ? 'primary' : 'secondary'} ${isDisabled ? 'translucent bg-light-danger' : ''} ${vertical ? 'w-100 text-left' : 'w-100'} text-nowrap mb-1 ${highlightedIds?.includes(subtrim._id) ? 'highlight-warn' : ''}`}
                        onClick={(e) => selectGroup(subtrim, e.ctrlKey || e.metaKey)}>
                        {shortenCommonWords(subtrim.name)}
                      </span></div>;
                  })}
                </div>;
              })
            }
          </div>
        </div>
      })}

    </div>
  } else if (loading) {
    return <div className={'p-3'}>Loading...</div>
  } else {
    return <div className={'p-3'}>No specs groups found</div>
  }
}
